// pages/OperationsPage.js - 运维管理
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Button, Modal, Steps, Timeline, Row, Col, message } from 'antd';
import { ToolOutlined, CheckCircleOutlined, ClockCircleOutlined, SyncOutlined, PlusOutlined } from '@ant-design/icons';
import { api } from '../services/api';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', solar: '#f4c542', text: '#fff', muted: 'rgba(255,255,255,0.6)' };

const levelColor = { high: 'red', normal: 'orange', low: 'blue' };
const levelText = (t) => ({ high: t('operation.levelHigh'), normal: t('operation.levelNormal'), low: t('operation.levelLow') });
const statusText = (t) => ({ pending: t('operation.statusPending'), processing: t('operation.statusProcessing'), done: t('operation.statusDone') });
const statusIcon = {
  pending: <ClockCircleOutlined style={{ color: '#faad14' }} />,
  processing: <SyncOutlined spin style={{ color: '#1890ff' }} />,
  done: <CheckCircleOutlined style={{ color: '#52c41a' }} />
};

const OperationsPage = () => {
  const { t } = useTranslation();
  const [workOrders, setWorkOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, processing: 0, done: 0, monthDone: 0 });
  const [loading, setLoading] = useState(true);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const json = await api.getWorkOrderList({ pageSize: 100 });
      if (json.success) setWorkOrders(json.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const json = await api.getWorkOrderStats();
      if (json.success) setStats(json.stats || { total: 0, pending: 0, processing: 0, done: 0, monthDone: 0 });
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchOrders(); fetchStats(); }, [fetchOrders, fetchStats]);

  const columns = [
    { title: t('operation.orderNo'), dataIndex: 'id', key: 'id', render: val => <span style={{ color: COLORS.primary }}>{val}</span> },
    { title: t('operation.device'), dataIndex: 'device', key: 'device', render: val => <span style={{ color: COLORS.text }}>{val}</span> },
    { title: t('operation.type'), dataIndex: 'type', key: 'type', render: val => <span style={{ color: COLORS.muted }}>{val}</span> },
    { title: t('operation.levelCol'), dataIndex: 'level', key: 'level', render: l => <Tag color={levelColor[l]}>{levelText(t)[l] || l}</Tag> },
    { title: t('operation.statusCol'), dataIndex: 'status', key: 'status', render: s => <Tag icon={statusIcon[s]}>{statusText(t)[s] || s}</Tag> },
    { title: t('operation.createdAt'), dataIndex: 'created_at', key: 'created_at', render: val => <span style={{ color: COLORS.muted }}>{val ? val.substring(0, 16) : '-'}</span> },
    { title: t('operation.action'), key: 'action', render: (_, record) => (
      <Button type="link" style={{ color: COLORS.primary }} onClick={() => { setSelected(record); setDetailVisible(true); }}>{t('operation.viewDetail')}</Button>
    )},
  ];

  return (
    <div style={{ minHeight: '100%' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: t('operation.pendingOrders'), value: stats.pending, color: '#faad14' },
          { label: t('operation.processing'), value: stats.processing, color: '#1890ff' },
          { label: t('operation.monthDone'), value: stats.monthDone, color: '#52c41a' },
        ].map(item => (
          <Col xs={24} sm={8} key={item.label}>
            <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(0,180,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,180,216,0.3)' }}>
                  <ToolOutlined style={{ fontSize: 22, color: item.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{item.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #00d4ff' }}>{t('operation.title')}</div>
        </div>
        <Table columns={columns} dataSource={workOrders} loading={loading} rowKey="id" pagination={{ pageSize: 10 }} style={{ background: 'transparent' }} />
      </Card>

      <Modal title={t('operation.orderDetail')} open={detailVisible} className="dark-confirm-modal"
        onCancel={() => setDetailVisible(false)}
        footer={null}
        styles={{ body: { background: '#0d1f44', color: '#fff' }, header: { background: '#0d1f44', color: '#fff' } }}
      >
        {selected && (
          <div style={{ background: 'transparent' }}>
            <Steps current={selected.status === 'done' ? 2 : selected.status === 'processing' ? 1 : 0} style={{ marginBottom: 24, background: 'transparent' }}
              items={[
                { title: t('operation.workOrderCreated'), description: selected.created_at ? selected.created_at.substring(0, 16) : '' },
                { title: t('operation.processingStep'), description: selected.assignee || '-' },
                { title: t('operation.completed'), description: '' }
              ]}
            />
            <div style={{ color: COLORS.muted }}>
              <p style={{ marginBottom: 8 }}><b style={{ color: COLORS.text }}>{t('operation.workOrderNo')}:</b> {selected.id}</p>
              <p style={{ marginBottom: 8 }}><b style={{ color: COLORS.text }}>{t('operation.deviceLabel')}:</b> {selected.device}</p>
              <p style={{ marginBottom: 8 }}><b style={{ color: COLORS.text }}>{t('operation.typeLabel')}:</b> {selected.type}</p>
              <p style={{ marginBottom: 8 }}><b style={{ color: COLORS.text }}>{t('operation.levelLabel')}:</b> {levelText(t)[selected.level] || selected.level}</p>
              <p style={{ marginBottom: 8 }}><b style={{ color: COLORS.text }}>{t('operation.statusLabel')}:</b> {statusText(t)[selected.status] || selected.status}</p>
              <p style={{ marginBottom: 8 }}><b style={{ color: COLORS.text }}>{t('operation.assignee')}:</b> {selected.assignee || '-'}</p>
              <p style={{ marginBottom: 8 }}><b style={{ color: COLORS.text }}>{t('operation.createdAtLabel')}:</b> {selected.created_at ? selected.created_at.substring(0, 19) : '-'}</p>
            </div>
            <Timeline style={{ marginTop: 24, background: 'transparent' }}
              items={[
                { children: `${t('operation.workOrderCreatedDesc')} - ${selected.created_at ? selected.created_at.substring(0, 16) : '-'}` },
                { children: `${t('operation.assignedTo')} ${selected.assignee || '-'}` },
                selected.status === 'done' ? { children: t('operation.recovered') } : { children: t('operation.inProcessing') }
              ]}
            />
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <Button onClick={() => setDetailVisible(false)} style={{ background: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,180,216,0.3)' }}>{t('operation.close')}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OperationsPage;
